import React, { useState } from 'react';

import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, CircleDashed, Layers3, Rows3, UserRound } from 'lucide-react';
import { AlineTooltip } from 'src/components';

import { TASK_GROUP_BY, TASK_GROUP_BY_LABEL } from './phase-task-groups';
import type { TaskGroupBy } from './phase-task-groups';
import { usePhasesHeaderStyles } from './PhasesHeader';

const GROUP_BY_OPTIONS = [
  TASK_GROUP_BY.None,
  TASK_GROUP_BY.Status,
  TASK_GROUP_BY.Assignee,
] as const;

const GROUP_BY_ICON = {
  [TASK_GROUP_BY.Assignee]: UserRound,
  [TASK_GROUP_BY.None]: Rows3,
  [TASK_GROUP_BY.Status]: CircleDashed,
} satisfies Record<TaskGroupBy, LucideIcon>;

interface TaskGroupByMenuProps {
  onChange: (value: TaskGroupBy) => void;
  value: TaskGroupBy;
}

export function TaskGroupByMenu({ onChange, value }: TaskGroupByMenuProps) {
  const { classes } = usePhasesHeaderStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <AlineTooltip arrow placement="top" title="Group by">
        <button
          aria-label="Group by"
          className={classes.pillButton}
          type="button"
          onClick={event => setAnchor(event.currentTarget)}
        >
          <Layers3 size={14} />
          {value === TASK_GROUP_BY.None ? 'No grouping' : TASK_GROUP_BY_LABEL[value]}
          <ChevronDown size={14} />
        </button>
      </AlineTooltip>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchor)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setAnchor(null)}
      >
        {GROUP_BY_OPTIONS.map(option => {
          const Icon = GROUP_BY_ICON[option];
          return (
            <MenuItem
              key={option}
              dense
              selected={option === value}
              onClick={() => {
                onChange(option);
                setAnchor(null);
              }}
            >
              <ListItemIcon className={classes.menuIcon}>
                <Icon size={18} />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: classes.menuPrimary }}
                primary={TASK_GROUP_BY_LABEL[option]}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
